/** Профиль бренда и аудит-журнал. */
import { ApiError } from '@allswipes/client';
import { Badge, BrandMark, Button, Card, Empty, Field, Skeleton, fmtDateTime, num, REGIONS } from '@allswipes/ui';
import { useEffect, useState } from 'react';

import { api } from '../api';
import { Layout } from '../components/Layout';
import { useResource } from '../hooks';
import { useConsole } from '../session';
import { useToast } from '../toast';

export function BrandScreen() {
  const { me, refresh } = useConsole();
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    category: '',
    regions: [] as string[],
    organizer: '',
    legal_id: '',
    contact: '',
    about: '',
    logo_url: '',
  });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!me) return;
    setForm({
      name: me.brand.name,
      category: me.brand.category,
      regions: [...me.brand.regions],
      organizer: me.brand.organizer,
      legal_id: me.brand.legal_id,
      contact: me.brand.contact,
      about: me.brand.about,
      logo_url: me.brand.logo_url ?? '',
    });
  }, [me]);

  const readOnly = me?.member.role !== 'owner';

  const save = async () => {
    setBusy(true);
    setErrors({});
    try {
      await api.saveBrand(form);
      await refresh();
      toast('Профиль бренда сохранён');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors(err.fields ?? {});
        toast(err.message);
      } else {
        toast('Не получилось сохранить');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!me) {
    return (
      <Layout title="Бренд">
        <Skeleton count={2} height={120} />
      </Layout>
    );
  }

  const toggleRegion = (code: string) =>
    setForm((f) => ({
      ...f,
      regions: f.regions.includes(code) ? f.regions.filter((r) => r !== code) : [...f.regions, code],
    }));

  return (
    <Layout
      title="Бренд"
      subtitle="эти данные подставляются в новые акции и в правила"
      action={
        !readOnly && (
          <Button variant="pri" disabled={busy} onClick={() => void save()}>
            {busy ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        )
      }
    >
      {readOnly && (
        <Card style={{ marginBottom: 16 }}>
          <Badge tone="warn">только просмотр</Badge>
          <p className="sub" style={{ marginTop: 8 }}>
            Профиль бренда меняет владелец. У твоей роли ({me.member.role}) этого права нет.
          </p>
        </Card>
      )}

      <div className="grid c2">
        <Card>
          <div className="h2" style={{ marginBottom: 12 }}>
            Основное
          </div>
          <div className="row" style={{ marginBottom: 14 }}>
            <BrandMark name={form.name || '?'} logoUrl={form.logo_url} size={48} />
            <div style={{ minWidth: 0 }}>
              <div className="h2">{form.name || 'Без названия'}</div>
              <div className="faint" style={{ fontSize: 12 }}>
                /{me.brand.slug}
              </div>
            </div>
          </div>

          <Field label="Название" error={errors.name}>
            <input
              className="input"
              value={form.name}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          <Field
            label="Категория"
            hint={
              me.brand.sphere_label
                ? `В ленте покупателя бренд попадает в сферу «${me.brand.sphere_label}»`
                : 'По категории бренд раскладывается по сферам в ленте покупателя'
            }
          >
            <input
              className="input"
              value={form.category}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="Напитки"
            />
          </Field>
          <Field label="Ссылка на логотип" hint="PNG или SVG на прозрачном фоне">
            <input
              className="input mono"
              value={form.logo_url}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
            />
          </Field>
          <Field label="Регионы работы">
            <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
              {REGIONS.map(({ code, name: label }) => (
                <button
                  key={code}
                  className="badge"
                  disabled={readOnly}
                  aria-pressed={form.regions.includes(code)}
                  style={
                    form.regions.includes(code)
                      ? { background: 'var(--accent)', color: 'var(--accent-ink)', borderColor: 'transparent' }
                      : undefined
                  }
                  onClick={() => toggleRegion(code)}
                >
                  {label}
                </button>
              ))}
            </div>
          </Field>
        </Card>

        <Card>
          <div className="h2" style={{ marginBottom: 12 }}>
            Для правил и страницы акции
          </div>
          <Field label="Организатор (юридическое лицо)" error={errors.organizer}>
            <input
              className="input"
              value={form.organizer}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, organizer: e.target.value })}
              placeholder="ООО «Название»"
            />
          </Field>
          <Field label="Реквизиты">
            <input
              className="input"
              value={form.legal_id}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, legal_id: e.target.value })}
            />
          </Field>
          <Field label="Контакт для участников" error={errors.contact}>
            <input
              className="input"
              value={form.contact}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
            />
          </Field>
          <Field label="О бренде">
            <textarea
              className="textarea"
              rows={5}
              value={form.about}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, about: e.target.value })}
            />
          </Field>
        </Card>
      </div>

      <Card style={{ marginTop: 16 }}>
        <div className="h2" style={{ marginBottom: 10 }}>
          Показатели бренда
        </div>
        <div className="stats">
          <div className="stat">
            <div className="v">{num(me.stats.campaigns)}</div>
            <div className="l">акций всего</div>
          </div>
          <div className="stat">
            <div className="v">{num(me.stats.participants)}</div>
            <div className="l">участников</div>
          </div>
          <div className="stat">
            <div className="v">{num(me.stats.prizes_given)}</div>
            <div className="l">призов выдано</div>
          </div>
        </div>
      </Card>
    </Layout>
  );
}

export function AuditScreen() {
  const { me } = useConsole();
  const log = useResource(() => api.audit(), []);

  return (
    <Layout
      title="Аудит-журнал"
      subtitle="кто и что делал с акциями, кодами и призами — журнал только дописывается"
    >
      {log.loading && <Skeleton count={6} height={44} />}
      {log.data?.entries.length === 0 && <Empty icon="scroll" title="Записей пока нет" />}

      {log.data && log.data.entries.length > 0 && (
        <Card className="flush">
          <table className="tbl" style={{ padding: 15 }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 15 }}>Когда</th>
                <th>Кто</th>
                <th>Действие</th>
                <th>Объект</th>
                <th>Детали</th>
              </tr>
            </thead>
            <tbody>
              {log.data.entries.map((entry) => (
                <tr key={entry.id}>
                  <td className="faint" style={{ fontSize: 12, paddingLeft: 15 }}>
                    {fmtDateTime(entry.created_at)}
                  </td>
                  <td className="mono" style={{ fontSize: 12 }}>
                    {entry.actor}
                  </td>
                  <td>{entry.action}</td>
                  <td className="faint mono" style={{ fontSize: 11.5 }}>
                    {entry.entity}
                  </td>
                  <td
                    className="faint mono"
                    style={{ fontSize: 11, maxWidth: 320, overflowWrap: 'anywhere' }}
                  >
                    {entry.payload ? JSON.stringify(entry.payload) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <p className="sub" style={{ marginTop: 14, fontSize: 12 }}>
        Журнал пишется на каждую операцию с кодами, призами и баллами и не имеет путей изменения или
        удаления: журнал, который можно поправить, ни на один вопрос не отвечает.
        {me && ` Показан журнал бренда «${me.brand.name}».`}
      </p>
    </Layout>
  );
}
